import React from 'react';
import styled from 'styled-components';
import { Modal, useConfirm, NotificationItem } from '@packages/ui/composites';
import { palette, rem } from '@packages/ui/tokens';
import { Icon } from '@packages/ui';
import { formatDateTime } from '../utils/formatDate';

export interface NotificationModalItem {
  notificationId?: string;
  message?: string | null;
  notificationDateTime?: string;
  isSeen?: boolean;
  notificationRedirectionPage?: string | null;
}

export interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: NotificationModalItem[];
  loading?: boolean;
  deletingId?: string;
  onItemClick?: (item: NotificationModalItem) => void;
  onDelete?: (notificationId: string) => void;
  onClearAll?: () => void;
  formatDate?: (dateStr: string) => string;
  emptyMessage?: string;
  id?: string;
}

const ClearAllButton = styled.button`
  background: ${palette.CulturedWhite};
  border: none;
  border-radius: ${rem(35)};
  font-weight: 500;
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  padding: ${rem(5)} ${rem(18)};
  cursor: pointer;
`;

const EmptyState = styled.div`
  padding: ${rem(40)} ${rem(24)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${rem(12)};
  color: ${palette.SpanishGray};
  font-size: ${rem(14)};
  .material-symbols-outlined {
    font-size: ${rem(50)};
  }
`;


export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  notifications,
  loading,
  deletingId,
  onItemClick,
  onDelete,
  onClearAll,
  formatDate = formatDateTime,
  emptyMessage = 'You currently have no notifications',
  id,
}) => {
  const { confirm } = useConfirm();

  const handleClearAll = async () => {
    const { confirmed } = await confirm({
      title: 'Are you sure you want to clear all notifications ?',
      actionLabel: 'Confirm',
      cancelLabel: 'No',
      semanticId: 'clearAllNotificationsConfirm',
    });
    if (confirmed) {
      onClearAll?.();
    }
  };

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}>
      <Modal.Portal>
        <Modal.Overlay closeOnOverlayClick />
        <Modal.Content size='sm' data-testid={id} style={{ overflow: 'hidden' }}>
          <Modal.Header>
            <Modal.Title>Notifications</Modal.Title>
            {notifications.length > 0 && onClearAll ? (
              <ClearAllButton type='button' onClick={handleClearAll}>
                Clear All
              </ClearAllButton>
            ) : (
              <Modal.Close />
            )}
          </Modal.Header>
          <Modal.Body loading={loading} style={{ padding: 0, overflowY: 'scroll' }}>
            {notifications.length > 0 ? (
              <div>
                {notifications.map((item) => (
                  <NotificationItem
                    key={item.notificationId}
                    status={item.isSeen ? 'seen' : 'unseen'}
                    dangerousHtml={item.message ?? ''}
                    title=""
                    date={item.notificationDateTime ? formatDate(item.notificationDateTime) : undefined}
                    onTitleClick={() => onItemClick?.(item)}
                    action={
                      onDelete ? (
                        <NotificationItem.Delete
                          onClick={() => onDelete(item.notificationId ?? '')}
                          loading={deletingId === item.notificationId}
                        />
                      ) : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState>
                <Icon name='info' size='xl' />
                <div className='text-center'>{emptyMessage}</div>
              </EmptyState>
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
};
